import { useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMe } from "../api/me";
import type { AssetDetail } from "../api/types";
import type { ViewerHandle } from "../viewer/Viewer";
import { TrueRenderButton } from "./TrueRenderButton";
import {
  INSPECTOR_GRID,
  Inspector,
  InspectorHeader,
  TryOnCards,
  UsedInAds,
  Viewer,
  ViewerSlot,
} from "./inspector";

/** Artwork is rendered as it is: nothing is put on it. */
const BARE = { materials: {}, skins: {}, colors_hex: {} };
const NO_POSES = {};

const AREA_WORDS: Record<string, string> = {
  flat: "Flat",
  cylinder: "Wraps around",
  wrap: "Its own texture",
};

function SkinInspector(props: { detail: AssetDetail; onExpired(): void }) {
  const { detail } = props;
  const meta = detail.meta;
  const subject = useMemo(() => ({ kind: "skin" as const, detail }), [detail]);
  const viewerRef = useRef<ViewerHandle>(null);
  const stills = useMe().data?.features?.stills === true;
  const target = useMemo(
    () => ({ kind: "skin" as const, id: detail.id }),
    [detail.id],
  );
  const px = Array.isArray(meta.px) ? (meta.px as number[]) : null;
  const area = AREA_WORDS[String(meta.region_type)];
  const tryOn = detail.try_on ?? [];
  return (
    <>
      <InspectorHeader detail={detail} />
      <div className={INSPECTOR_GRID}>
        <ViewerSlot>
          <Viewer
            ref={viewerRef}
            subject={subject}
            onExpired={props.onExpired}
          />
        </ViewerSlot>
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-1" data-testid="facts">
            {px ? (
              <p className="m-0 text-sm font-semibold text-slate-800">
                {px[0]} × {px[1]} px
              </p>
            ) : null}
            {area ? <p className="m-0 text-sm text-slate-700">{area}</p> : null}
            <p className="m-0 text-sm text-slate-700">
              {meta.emissive ? "Screen" : "Printed"}
            </p>
          </section>
          {stills ? (
            <TrueRenderButton
              target={target}
              dressing={BARE}
              poses={NO_POSES}
              viewerRef={viewerRef}
            />
          ) : null}
          <section className="flex flex-col gap-3">
            <h2 className="tb-h-row m-0">Try it on</h2>
            {tryOn.length > 0 ? (
              <TryOnCards
                items={tryOn}
                to={(item) =>
                  `/assets/shape/${item.id}?skin=${item.slot}:${detail.id}`
                }
              />
            ) : (
              <p className="m-0 text-sm text-slate-700">
                No object in the library has an area this shape.
              </p>
            )}
          </section>
        </div>
      </div>
      <UsedInAds detail={detail} />
    </>
  );
}

/** `/assets/skin/:id`: an artwork, flat, with what it is and the objects it
 *  can be tried on. */
export function AssetSkinPage() {
  const { id = "" } = useParams();
  return (
    <Inspector kind="skin" id={id}>
      {(detail, asset) => (
        <SkinInspector
          key={detail.id}
          detail={detail}
          onExpired={asset.onExpired}
        />
      )}
    </Inspector>
  );
}
